import { Grid, MenuItem, Typography, Switch } from '@mui/material';
import { withStyles } from '@mui/styles';
import React, { useEffect, useCallback, useState, useMemo, useContext, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { toInteger } from 'lodash';
import moment from 'moment';
import { COLORS } from '../../../utils/Application_Constants';
import { states } from '../../Configurations/ExpenseEntryConfig';
import { getClinician, getClinicianAssignments } from '../../Services/ExpenseService';
import { valueAsNumber, valueAsText } from '../../Utils/ValueAsUtils';
import BookingNumberInputAdornment from './BookingNumberInputAdornment';
import ExpenseAutocomplete from './ExpenseAutocomplete';
import ExpenseTextField from './ExpenseTextField';
import { BillTypeEnum } from './BillType.enum';
import Alert from '../Alert';
import eventBus from './EventBus';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { ExpenseModeEnum } from './ExpenseMode.enum';
import { ValidationModeContext } from '../../../contexts/VendorPaymentValidationModeContext';
import ValidationModeEnum from './ValidationMode.enum';
import { DivisionEnum, ExcludedDivisionEnum } from './Division.enum';

const styles = () => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
});

const BookingDetailsForm = (props) => {
    const {
        classes,
        index,
        expense,
        divisions,
        setIsSGA,
        mode,
        isSGA,
        originalExpenseEntries,
        isViewOnly,
        isInvoiceReviewLocked = false,
        onAssignmentChange,
        enableEditByCorrection = false,
    } = props;
    const { control, watch, errors, setValue, getValues, clearErrors } = useFormContext();
    const bookingNumber = watch(`expenseEntries[${index}].bookingId`);
    const expenseEntries = watch('expenseEntries');
    const billType = watch(`expenseEntries[${index}].billType`);
    const bookingNumberRequired = Boolean(
        billType &&
            (billType === BillTypeEnum.Billable || billType === BillTypeEnum.NonBillProducer)
    );
    const isNonBillTeam = billType === BillTypeEnum.NonBillTeam;
    const validationMode = useContext(ValidationModeContext);
    const isBillTypeSGA = billType === BillTypeEnum.SGA;
    const isCreditCard = mode === ExpenseModeEnum.CreditCard;
    const currentStatus = watch(`expenseEntries[${index}].status`);
    const creditCardFieldsRequired =
        !isCreditCard ||
        currentStatus === ExpenseStatusValuesEnum.Approved ||
        currentStatus === ExpenseStatusValuesEnum.InvoiceReview;
    const clinicianIdRequired =
        !(mode === ExpenseModeEnum.CreditCard && (isBillTypeSGA || isNonBillTeam)) &&
        expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
        !(mode === ExpenseModeEnum.Vault && validationMode === ValidationModeEnum.Save) &&
        creditCardFieldsRequired;
    const clinicianFirstNameRequired =
        !isBillTypeSGA &&
        expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
        !(mode === ExpenseModeEnum.Vault && validationMode === ValidationModeEnum.Save) &&
        !(mode === ExpenseModeEnum.CreditCard && isNonBillTeam) &&
        creditCardFieldsRequired;
    const clinicianLastNameRequired =
        !isBillTypeSGA &&
        expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
        !(mode === ExpenseModeEnum.CreditCard && isNonBillTeam) &&
        creditCardFieldsRequired;
    const clinicianId = watch(`expenseEntries[${index}].clinicianId`);
    const primaryClinicianFirstName = getValues('expenseEntries[0].clinicianFirstName');
    const primaryClinicianLastName = getValues('expenseEntries[0].clinicianLastName');
    const primaryClinicianId = watch('expenseEntries[0].clinicianId');
    const primaryBookingId = watch('expenseEntries[0].bookingId');
    const bookingBillType = watch(`expenseEntries[${index}].bookingBillType`);
    const [alert, setAlert] = useState();
    const [assignment, setAssignment] = useState(null);
    const [startDateChangedEventData, setStartDateChangedEventData] = useState(null);
    const [txnDateChangedEventData, setTxnDateChangedEventData] = useState(null);
    const isPrimary = index === 0;
    // Salesperson and recruiter are derived from the booking's team, but the values the expense
    // was loaded with have to survive the initial render pass, before the assignment fetch
    // resolves. Preserve them until the booking actually moves off the one the expense was loaded
    // with, and once it has, keep recomputing -- even if the original booking is reselected later.
    const originalEntry = originalExpenseEntries?.[index];
    const hasOriginalBookingSalesInfo = Boolean(
        originalEntry?.salespersonName || originalEntry?.recruiterName
    );
    const bookingMatchesOriginal =
        hasOriginalBookingSalesInfo &&
        toInteger(bookingNumber) === toInteger(originalEntry.bookingId);
    // The form is reset with the loaded entries in a separate render from originalExpenseEntries
    // being set, so only treat a mismatch as a change once the two have lined up at least once.
    const originalBookingLoadedRef = useRef(false);
    const bookingChangedRef = useRef(false);
    if (bookingMatchesOriginal) {
        originalBookingLoadedRef.current = true;
    } else if (originalBookingLoadedRef.current) {
        bookingChangedRef.current = true;
    }
    const usingOriginalBookingSalesInfo = hasOriginalBookingSalesInfo && !bookingChangedRef.current;
    // Get the original status from the originalExpenseEntries
    const originalStatus = useMemo(() => {
        if (expense.id && originalExpenseEntries?.length > 0) {
            const originalExpense = originalExpenseEntries.find((entry) => entry.id === expense.id);
            return originalExpense ? originalExpense.status : expense.status;
        }
        return expense.status; // Fallback to current status if no original entry is found
    }, [expense.id, originalExpenseEntries, expense.status]);
    // Compute isPosted based on original status and current transient state
    const isPosted = useMemo(
        () =>
            originalStatus === ExpenseStatusValuesEnum.Posted ||
            (currentStatus === ExpenseStatusValuesEnum.OnHoldReimbursement &&
                originalStatus === ExpenseStatusValuesEnum.Posted),
        [originalStatus, currentStatus]
    );
    const isPendingSubmission = expense.status === ExpenseStatusValuesEnum.PendingSubmission;

    useEffect(() => {
        if (!creditCardFieldsRequired) {
            clearErrors([
                `expenseEntries[${index}].bookingId`,
                `expenseEntries[${index}].clinicianId`,
                `expenseEntries[${index}].clinicianFirstName`,
                `expenseEntries[${index}].clinicianLastName`,
                `expenseEntries[${index}].division`,
                `expenseEntries[${index}].salespersonName`,
                `expenseEntries[${index}].recruiterName`,
            ]);
        }
    }, [creditCardFieldsRequired, index, clearErrors]);

    useEffect(() => {
        const handleStartDateChanged = (event) => {
            const date = moment(event.data, 'YYYY-MM-DD', true); // Strict format check
            if (date.isValid()) {
                setStartDateChangedEventData(date);
            } else {
                setStartDateChangedEventData(null);
            }
        };

        const handleTxnDateChanged = (event) => {
            const date = moment(event.data, 'YYYY-MM-DD', true); // Strict format check
            if (date.isValid()) {
                setTxnDateChangedEventData(date);
            } else {
                setTxnDateChangedEventData(null);
            }
        };

        eventBus.on(`startDateChanged${index}`, handleStartDateChanged);
        eventBus.on(`txnDateChanged${index}`, handleTxnDateChanged);

        return () => {
            eventBus.off(`startDateChanged${index}`, handleStartDateChanged); // Cleanup
            eventBus.off(`txnDateChanged${index}`, handleTxnDateChanged); // Cleanup
        };
    }, []); // Empty dependency array ensures it runs only once

    const getBeforeBookingTeam = (assignmentParam, baseExpenseCompareDate) => {
        if (!assignmentParam?.bookingTeams || assignmentParam.bookingTeams.length === 0) {
            return null;
        }

        let beforeBookingTeam = assignmentParam.bookingTeams
            .filter(
                (x) =>
                    baseExpenseCompareDate &&
                    moment(x.effectiveDate)
                        .startOf('day')
                        .isSameOrBefore(baseExpenseCompareDate, 'day')
            )
            .at(-1); // Equivalent to LastOrDefault()

        if (!beforeBookingTeam && baseExpenseCompareDate) {
            beforeBookingTeam = assignmentParam.bookingTeams[0] ?? null; // Equivalent to FirstOrDefault()
        }

        return beforeBookingTeam;
    };

    function setDivision(division) {
        const divName = divisions
            .filter((res) => res.description === division)
            .map((ele) => ele.name);
        if (divName.length === 1) {
            const divisionValue =
                divName[0] === ExcludedDivisionEnum.EmergencyMedicine
                    ? DivisionEnum.HospitalMedicine
                    : divName[0];

            setValue(`expenseEntries[${index}].division`, divisionValue, {
                shouldValidate: true,
            });
        } else {
            setValue(`expenseEntries[${index}].division`, '', {
                shouldValidate: true,
            });
        }
    }

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                let response = null;
                if (bookingNumber) {
                    response = await getClinicianAssignments(bookingNumber);
                }

                if (response) {
                    if (response.clinicianFirstName) {
                        setValue(
                            `expenseEntries[${index}].clinicianFirstName`,
                            response.clinicianFirstName,
                            { shouldValidate: true }
                        );
                    }

                    if (response.clinicianLastName) {
                        setValue(
                            `expenseEntries[${index}].clinicianLastName`,
                            response.clinicianLastName,
                            { shouldValidate: true }
                        );
                    }

                    if (
                        !getValues(`expenseEntries[${index}].clinicianId`) &&
                        response.clinicianId
                    ) {
                        setValue(`expenseEntries[${index}].clinicianId`, response.clinicianId, {
                            shouldValidate: true,
                        });
                    }

                    if (!getValues(`expenseEntries[${index}].clientId`) && response.clientId) {
                        setValue(`expenseEntries[${index}].clientId`, response.clientId, {
                            shouldValidate: true,
                        });
                    }

                    if (!getValues(`expenseEntries[${index}].division`) && response.division) {
                        setDivision(response.division);
                    }

                    if (!getValues(`expenseEntries[${index}].state`) && response.location?.state) {
                        setValue(`expenseEntries[${index}].state`, response.location.state, {
                            shouldValidate: true,
                        });
                    }
                }

                setAssignment(response); // Pre-fill form data
                onAssignmentChange(index, response); // Pass the assignment data to the parent component
            } catch (error) {
                console.error('Error fetching Clinician Assignment data:', error);
            }
        };

        fetchInitialData();
    }, [primaryClinicianFirstName, primaryClinicianLastName, bookingNumber]);

    // Reset Salesperson and Recruiter as needed when Start Date, Txn Date, or Assignment Changes
    useEffect(() => {
        if (usingOriginalBookingSalesInfo) {
            // Still on the booking the expense was loaded with -- keep its original sales info
            return;
        }
        console.log(
            `txnDateChangedEventData${index}: ${txnDateChangedEventData} or startDateChangedEventData${index}: ${startDateChangedEventData} or  assignment changed:`
        );

        let salespersonName = null;
        let recruiterName = null;
        const searchDate = startDateChangedEventData ?? txnDateChangedEventData;

        if (assignment && searchDate) {
            const beforeBookingTeam = getBeforeBookingTeam(assignment, searchDate);
            salespersonName = beforeBookingTeam?.salesperson?.name;
            recruiterName = beforeBookingTeam?.recruiter?.name;
            setValue(`expenseEntries[${index}].salespersonName`, salespersonName);
            setValue(`expenseEntries[${index}].recruiterName`, recruiterName);
        } else {
            setValue(`expenseEntries[${index}].salespersonName`, '');
            setValue(`expenseEntries[${index}].recruiterName`, '');
        }
    }, [
        txnDateChangedEventData,
        startDateChangedEventData,
        assignment,
        usingOriginalBookingSalesInfo,
    ]);

    useEffect(() => {
        if (mode === ExpenseModeEnum.CreditCard) {
            console.log(isSGA[index], `- BookingDetailsForm isSGA ${index} Has changed`);
            if (isSGA[index]) {
                // TODO
            } else {
                // TODO
            }
        }
    }, [isSGA[index]]);

    const isAnySplitExpensePosted = (info) => {
        const hasPostedExpense = expenseEntries.some(
            (entry, idx) => idx !== 0 && entry.status === ExpenseStatusValuesEnum.Posted
        );
        if (hasPostedExpense) {
            const postedEntry = expenseEntries.find(
                (entry, idx) => idx !== 0 && entry.status === ExpenseStatusValuesEnum.Posted
            );
            if (
                postedEntry.clinicianId !== info.clinicianId ||
                postedEntry.clinicianFirstName !== info.clinicianFirstName ||
                postedEntry.clinicianLastName !== info.clinicianLastName
            ) {
                return true;
            }
        }
        return false;
    };

    // check if there are multiple entries
    const isMultiExpense = expenseEntries.length > 1;

    // check if the primary entries are changed
    const onPrimaryChange = useCallback(
        (key, value) => {
            // if primary changed && multiple entries -> show message
            if (isMultiExpense) {
                setAlert({ message: 'All transactions will be updated', type: 'warning' });
                // updated all expenses with primary value
                for (let i = 1; i < expenseEntries.length; i++) {
                    if (expenseEntries[i].status !== ExpenseStatusValuesEnum.Posted) {
                        setValue(`expenseEntries[${i}].${key}`, value);
                    }
                }
            }
        },
        [isMultiExpense, expenseEntries, primaryClinicianLastName]
    );

    const autoPopulateBookingNumberFields = (bookingInfo) => {
        if (bookingInfo) {
            if (!isPrimary) {
                if (
                    primaryClinicianId &&
                    Number(bookingInfo.clinicianId) !== Number(primaryClinicianId)
                ) {
                    setAlert({
                        message: 'The Clinician Id does not match the primary expense.',
                        type: 'error',
                    });
                    return false;
                }
                if (
                    primaryClinicianFirstName &&
                    bookingInfo.clinicianFirstName !== primaryClinicianFirstName
                ) {
                    setAlert({
                        message: 'The Clinician First Name does not match the primary expense.',
                        type: 'error',
                    });
                    return false;
                }
                if (
                    primaryClinicianLastName &&
                    bookingInfo.clinicianLastName !== primaryClinicianLastName
                ) {
                    setAlert({
                        message: 'The Clinician Last Name does not match the primary expense.',
                        type: 'error',
                    });
                    return false;
                }
            } else if (isAnySplitExpensePosted(bookingInfo)) {
                setAlert({
                    message:
                        'Cannot change clinician details because one of the split expenses is already posted.',
                    type: 'error',
                });
                return false;
            }
            setValue(`expenseEntries[${index}].bookingId`, bookingInfo.id, {
                shouldValidate: true,
            });
            setValue(`expenseEntries[${index}].clinicianId`, bookingInfo.clinicianId, {
                shouldValidate: true,
            });
            setValue(
                `expenseEntries[${index}].clinicianFirstName`,
                bookingInfo.clinicianFirstName,
                {
                    shouldValidate: true,
                }
            );

            setValue(`expenseEntries[${index}].clinicianLastName`, bookingInfo.clinicianLastName, {
                shouldValidate: true,
            });
            setValue(`expenseEntries[${index}].clientId`, bookingInfo.clientId, {
                shouldValidate: true,
            });
            setValue(`expenseEntries[${index}].billingAccountId`, bookingInfo.billingAccountId, {
                shouldValidate: true,
            });

            setDivision(bookingInfo.division);

            setValue(`expenseEntries[${index}].state`, bookingInfo.location.state, {
                shouldValidate: true,
            });
        } else {
            setValue(`expenseEntries[${index}].bookingId`, '');
            setValue(`expenseEntries[${index}].clinicianId`, '');
            setValue(`expenseEntries[${index}].clinicianFirstName`, '');
            setValue(`expenseEntries[${index}].clinicianLastName`, '');
            setValue(`expenseEntries[${index}].clientId`, '');
            setValue(`expenseEntries[${index}].billingAccountId`, '');
            setValue(`expenseEntries[${index}].division`, '');
            setValue(`expenseEntries[${index}].state`, '');
        }
        if (isPrimary) onPrimaryChange('bookingId', bookingInfo?.id || '');
        if (isPrimary) onPrimaryChange('clinicianId', bookingInfo?.clinicianId || '');
        if (isPrimary) onPrimaryChange('clinicianFirstName', bookingInfo?.clinicianFirstName || '');
        if (isPrimary) onPrimaryChange('clinicianLastName', bookingInfo?.clinicianLastName || '');
        if (isPrimary) onPrimaryChange('clientId', bookingInfo?.facilityId || '');
        if (isPrimary) onPrimaryChange('division', bookingInfo?.division || '');
        if (isPrimary) onPrimaryChange('state', bookingInfo?.location?.state || '');
        setAssignment(bookingInfo);
        return true;
    };

    const autoPopulateClinicianFields = (clinicianInfo) => {
        if (clinicianInfo) {
            if (!isPrimary) {
                if (primaryClinicianId && Number(clinicianInfo.id) !== Number(primaryClinicianId)) {
                    setAlert({
                        message: 'The Clinician Id does not match the primary expense.',
                        type: 'error',
                    });
                    return false;
                }
                if (
                    primaryClinicianFirstName &&
                    clinicianInfo.firstName !== primaryClinicianFirstName
                ) {
                    setAlert({
                        message: 'The Clinician First Name does not match the primary expense.',
                        type: 'error',
                    });
                    return false;
                }
                if (
                    primaryClinicianLastName &&
                    clinicianInfo.lastName !== primaryClinicianLastName
                ) {
                    setAlert({
                        message: 'The Clinician Last Name does not match the primary expense.',
                        type: 'error',
                    });
                    return false;
                }
            } else if (isAnySplitExpensePosted(clinicianInfo)) {
                setAlert({
                    message:
                        'Cannot change clinician details because one of the split expenses is already posted.',
                    type: 'error',
                });
                return false;
            }
            setValue(`expenseEntries[${index}].clinicianId`, clinicianInfo.id, {
                shouldValidate: true,
            });
            setValue(`expenseEntries[${index}].clinicianFirstName`, clinicianInfo.firstName, {
                shouldValidate: true,
            });
            setValue(`expenseEntries[${index}].clinicianLastName`, clinicianInfo.lastName, {
                shouldValidate: true,
            });
        } else {
            setValue(`expenseEntries[${index}].clinicianId`, '');
            setValue(`expenseEntries[${index}].clinicianFirstName`, '');
            setValue(`expenseEntries[${index}].clinicianLastName`, '');
        }
        if (isPrimary) onPrimaryChange('clinicianId', clinicianInfo?.id || '');
        if (isPrimary) onPrimaryChange('clinicianFirstName', clinicianInfo?.firstName || '');
        if (isPrimary) onPrimaryChange('clinicianLastName', clinicianInfo?.lastName || '');
        return true;
    };

    const renderClinicianId = (onChange, value) =>
        bookingNumber ? (
            <ExpenseTextField
                id="clinician-id"
                inputProps={{ maxLength: 9 }}
                onChange={(e) => onChange(valueAsNumber(e.target.value))}
                value={value}
                disabled={isViewOnly || isInvoiceReviewLocked || Boolean(bookingNumber)}
                label="Clinician ID"
                placeholder="Clinician ID"
                required={clinicianIdRequired}
                error={Boolean(errors.expenseEntries?.[index]?.clinicianId)}
                helperText={errors.expenseEntries?.[index]?.clinicianId?.message}
            />
        ) : (
            <ExpenseAutocomplete
                id="clinician-id"
                inputProps={{ maxLength: 9, disableUnderline: true }}
                onChange={(e) => {
                    const newValue = e ? `${e.id}` : null;
                    if (isPrimary) onPrimaryChange('clinicianFirstName', newValue);
                    const shouldUpdate = autoPopulateClinicianFields(e);
                    if (!shouldUpdate) {
                        onChange(primaryClinicianFirstName);
                    } else {
                        onChange(newValue);
                    }
                }}
                value={value}
                label="Clinician ID"
                required={clinicianIdRequired}
                isViewOnly={
                    isViewOnly ||
                    isInvoiceReviewLocked ||
                    isPosted ||
                    (isPendingSubmission && mode !== ExpenseModeEnum.Payment)
                }
                error={Boolean(errors.expenseEntries?.[index]?.clinicianId)}
                helperText={errors.expenseEntries?.[index]?.clinicianId?.message}
                getOptionsMethod={getClinician}
                placeholder="Clinician ID"
                optionLabelKey="id"
                optionSelectedKey="id"
                valueAsMethod={valueAsNumber}
            />
        );

    return (
        <>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        Booking Details
                    </Grid>
                </Grid>
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].bookingId`}
                        defaultValue={expense.bookingId || null}
                        rules={{
                            required:
                                expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                                bookingNumberRequired &&
                                creditCardFieldsRequired
                                    ? 'Booking number is required'
                                    : false,
                        }}
                        disabled={isViewOnly || isInvoiceReviewLocked}
                        isViewOnly={isViewOnly || isInvoiceReviewLocked}
                        render={({ onChange, value }) => (
                            <ExpenseAutocomplete
                                id="booking-number"
                                onChange={(e) => {
                                    const newValue = e ? `${e.id}` : null;
                                    const shouldUpdate = autoPopulateBookingNumberFields(e);
                                    if (!shouldUpdate) {
                                        onChange(primaryBookingId);
                                    } else {
                                        onChange(newValue);
                                        if (typeof onAssignmentChange === 'function') {
                                            onAssignmentChange(index, e); // Pass the assignment data to the parent component
                                        }
                                    }
                                }}
                                value={value}
                                label="Booking number"
                                required={
                                    expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                                    bookingNumberRequired &&
                                    creditCardFieldsRequired
                                }
                                disabled={
                                    isViewOnly ||
                                    isPosted ||
                                    isInvoiceReviewLocked ||
                                    (isPendingSubmission &&
                                        mode !== ExpenseModeEnum.Payment &&
                                        !enableEditByCorrection)
                                }
                                isViewOnly={isViewOnly || isInvoiceReviewLocked}
                                startAdornment={<BookingNumberInputAdornment />}
                                error={Boolean(errors.expenseEntries?.[index]?.bookingId)}
                                helperText={errors.expenseEntries?.[index]?.bookingId?.message}
                                minLength={6}
                                inputProps={{ maxLength: 50, disableUnderline: true }}
                                getOptionsMethod={getClinicianAssignments}
                                optionLabelKey="name"
                                optionSelectedKey="id"
                                valueAsMethod={valueAsNumber}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].clinicianId`}
                        defaultValue={expense.clinicianId}
                        rules={{
                            required: clinicianIdRequired ? 'Clinician ID is required' : false,
                            validate: {
                                match: (value) =>
                                    index === 0 ||
                                    toInteger(value) === toInteger(primaryClinicianId) ||
                                    'Clinician IDs must match',
                            },
                        }}
                        render={({ onChange, value }) => renderClinicianId(onChange, value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].clinicianFirstName`}
                        defaultValue={expense.clinicianFirstName}
                        rules={{
                            required: clinicianFirstNameRequired
                                ? 'Clinician first name is required'
                                : false,
                            validate: {
                                match: (value) =>
                                    index === 0 ||
                                    value === primaryClinicianFirstName ||
                                    'Clinician first names must match',
                            },
                        }}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="clinician-first-name"
                                inputProps={{ maxLength: 50 }}
                                onChange={(e) => {
                                    if (isPrimary)
                                        onPrimaryChange('clinicianFirstName', e.target.value);
                                    return onChange(valueAsText(e.target.value));
                                }}
                                value={value}
                                label="Clinician first name"
                                required={clinicianFirstNameRequired}
                                placeholder="Clinician first name"
                                disabled={
                                    isViewOnly ||
                                    isInvoiceReviewLocked ||
                                    Boolean(bookingNumber || clinicianId)
                                }
                                error={Boolean(errors.expenseEntries?.[index]?.clinicianFirstName)}
                                helperText={
                                    errors.expenseEntries?.[index]?.clinicianFirstName?.message
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].clinicianLastName`}
                        rules={{
                            required: clinicianLastNameRequired
                                ? 'Clinician last name is required'
                                : false,
                            validate: {
                                match: (value) =>
                                    index === 0 ||
                                    value === primaryClinicianLastName ||
                                    'Clinician last names must match',
                            },
                        }}
                        defaultValue={expense.clinicianLastName}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="clinician-last-name"
                                inputProps={{ maxLength: 50 }}
                                onChange={(e) => {
                                    if (isPrimary)
                                        onPrimaryChange('clinicianLastName', e.target.value);
                                    return onChange(valueAsText(e.target.value));
                                }}
                                value={value}
                                label="Clinician last name"
                                required={clinicianLastNameRequired}
                                placeholder="Clinician last name"
                                disabled={
                                    isViewOnly ||
                                    isInvoiceReviewLocked ||
                                    Boolean(bookingNumber || clinicianId)
                                }
                                error={Boolean(errors.expenseEntries?.[index]?.clinicianLastName)}
                                helperText={
                                    errors.expenseEntries?.[index]?.clinicianLastName?.message
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].clientId`}
                        defaultValue={expense.clientId}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="client-id"
                                inputProps={{ maxLength: 50 }}
                                onChange={onChange}
                                value={value}
                                label="Client ID"
                                placeholder="Client ID"
                                disabled
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].division`}
                        defaultValue={expense.division}
                        rules={{
                            required:
                                !bookingNumber &&
                                expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                                creditCardFieldsRequired
                                    ? 'Division is required'
                                    : false,
                        }}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="division"
                                select
                                onChange={onChange}
                                value={value}
                                label="Division"
                                required={
                                    expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                                    creditCardFieldsRequired
                                }
                                disabled={
                                    isViewOnly || isInvoiceReviewLocked || Boolean(bookingNumber)
                                }
                                error={Boolean(errors.expenseEntries?.[index]?.division)}
                                helperText={errors.expenseEntries?.[index]?.division?.message}
                            >
                                <MenuItem value="" disabled>
                                    Select a division
                                </MenuItem>
                                {divisions.map((option, i) => (
                                    <MenuItem
                                        key={option.name}
                                        value={option.name}
                                        id={`division-menu-option-${i}`}
                                    >
                                        {option.description}
                                    </MenuItem>
                                ))}
                            </ExpenseTextField>
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].salespersonName`}
                        defaultValue={expense.salespersonName}
                        rules={{
                            required:
                                bookingBillType === BillTypeEnum.Billable &&
                                creditCardFieldsRequired
                                    ? 'Salesperson is required'
                                    : false,
                        }}
                        render={({ value }) => (
                            <ExpenseTextField
                                id="salespersonName"
                                inputProps={{ maxLength: 50 }}
                                value={value}
                                label="Salesperson"
                                required={
                                    bookingBillType === BillTypeEnum.Billable &&
                                    creditCardFieldsRequired
                                }
                                placeholder={
                                    assignment &&
                                    (!startDateChangedEventData || !txnDateChangedEventData)
                                        ? 'Requires start or transaction date'
                                        : 'Salesperson'
                                }
                                disabled
                                error={Boolean(errors.expenseEntries?.[index]?.salespersonName)}
                                helperText={
                                    errors.expenseEntries?.[index]?.salespersonName?.message
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].recruiterName`}
                        defaultValue={expense.recruiterName}
                        rules={{
                            required:
                                bookingBillType === BillTypeEnum.Billable &&
                                creditCardFieldsRequired
                                    ? 'Recruiter is required'
                                    : false,
                        }}
                        render={({ value }) => (
                            <ExpenseTextField
                                id="recruiterName"
                                inputProps={{ maxLength: 50 }}
                                value={value}
                                label="Recruiter"
                                required={
                                    bookingBillType === BillTypeEnum.Billable &&
                                    creditCardFieldsRequired
                                }
                                placeholder={
                                    assignment &&
                                    (!startDateChangedEventData || !txnDateChangedEventData)
                                        ? 'Requires start or transaction date'
                                        : 'Recruiter'
                                }
                                disabled
                                error={Boolean(errors.expenseEntries?.[index]?.recruiterName)}
                                helperText={errors.expenseEntries?.[index]?.recruiterName?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].state`}
                        defaultValue={expense.state}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="state-abbreviation"
                                onChange={onChange}
                                select
                                value={value}
                                label="State"
                                disabled={
                                    isViewOnly || isInvoiceReviewLocked || Boolean(bookingNumber)
                                }
                            >
                                <MenuItem disabled value="">
                                    Please select a state
                                </MenuItem>
                                {states.map(({ name, value: v }, i) => (
                                    <MenuItem
                                        key={v}
                                        value={v}
                                        id={`state-abbreviation-menu-option-${i}`}
                                    >
                                        {name}
                                    </MenuItem>
                                ))}
                            </ExpenseTextField>
                        )}
                    />
                </Grid>
            </Grid>
            <Alert alert={alert} setAlert={setAlert} />
        </>
    );
};

export default withStyles(styles)(BookingDetailsForm);
